import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import useSearchStore from '../../_global/js/SearchStore';
import toKebabCase from '../../_global/js/stringToKebab';

function FacetAccordion({ facetTitle, facetKey, facetParamName }) {
    const [accordionExpanded, setAccordionExpanded] = useState(false);

    const searchFacetsJson = useSearchStore((state) =>
        state.getSearchFacetDataByKey(facetKey)
    );
    const selectedFacetOptions = useSearchStore((state) =>
        state.getSearchFacetSelectionsByKey(facetParamName)
    );
    const setSearchFacetSelectionsByKey = useSearchStore(
        (state) => state.setSearchFacetSelectionsByKey
    );

    /**
     * Operations to perform when the selcted options have updated
     */
    useEffect(() => {
        if (selectedFacetOptions.length !== 0 && !accordionExpanded) {
            setAccordionExpanded(true);
        }
    }, [selectedFacetOptions]);

    /**
     * Respond to clicks on the accordion button
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onAccordionClick = (evt) => {
        evt.preventDefault();

        setAccordionExpanded(!accordionExpanded);
    };

    /**
     * Respond to changes to the facet option inputs
     *
     * @param {Change Event} evt Event that triggered the change
     *
     * @returns void
     */
    const onFacetSelection = (evt) => {
        const userHasChecked = evt.target.checked;
        const checkboxValue = evt.target.getAttribute('name');
        const currentSelectedOptions = [...selectedFacetOptions];

        if (userHasChecked) {
            // Add this to the list of selected facets
            currentSelectedOptions.push(checkboxValue);
        } else {
            // Remove this from the list of selected facets
            const valueIndex = currentSelectedOptions.indexOf(checkboxValue);
            currentSelectedOptions.splice(valueIndex, 1);
        }

        setSearchFacetSelectionsByKey(facetParamName, currentSelectedOptions);
    };

    return (
        <div className="facet__group no-wysiwyg">
            <button
                aria-expanded={accordionExpanded}
                className={`facet-group__title ${
                    accordionExpanded ? 'facet-group__title--open' : ''
                }`}
                onClick={onAccordionClick}
                type="button"
            >
                <span className="facet-group__title-label">{facetTitle}</span>
                <div className="facet-group__icon-container">
                    <span className="facet-group__icon-vertical" />
                    <span className="facet-group__icon-horizontal" />
                </div>
            </button>
            <ul
                aria-hidden={!accordionExpanded}
                className={`facet-group__list facet-group__list--css-animation ${
                    accordionExpanded ? 'facet-group__list--active' : ''
                }`}
            >
                {searchFacetsJson.map((option) => {
                    let isChecked = false;

                    if (selectedFacetOptions.indexOf(option.value) !== -1) {
                        isChecked = true;
                    }

                    return (
                        <li
                            className="facet-group__list-item"
                            data-component="facet-group__list-item"
                            key={toKebabCase(option.label)}
                        >
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <label
                                className="facet-group__checkbox-label"
                                htmlFor={toKebabCase(option.label)}
                            >
                                <input
                                    className="facet-group__checkbox"
                                    type="checkbox"
                                    onChange={onFacetSelection}
                                    id={toKebabCase(option.label)}
                                    checked={isChecked}
                                    name={option.value}
                                />
                                <span
                                    className="pill input"
                                >
                                    {option.label}{' '}
                                    <span className="facet-group__results-number">
                                        ({option.availableItems})
                                    </span>
                                </span>
                            </label>
                        </li>
                    );
                })}
                {searchFacetsJson.length === 0 ? (
                    <p className="facet-group__no-options">
                        There are no {facetTitle} options available for the current search term or filters.
                    </p>
                ) : null}
            </ul>
        </div>
    );
}

export default React.memo(FacetAccordion);

FacetAccordion.propTypes = {
    facetTitle: PropTypes.string,
    facetKey: PropTypes.string,
    facetParamName: PropTypes.string,
};

FacetAccordion.defaultProps = {
    facetTitle: 'Resource Type',
    facetKey: '',
    facetParamName: '',
};
